import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import {
  quickPanelQuery,
  type ChatConversation,
  type ChatCustomer,
  type Comm,
  type QuickCompany,
  type QuickDevice,
  type QuickDeviceRow,
  type QuickKind,
  type QuickOffice,
  type QuickOwner,
  type QuickPerson,
  type QuickUser,
} from '../api/easeedeskQuick'
import { Spinner } from '../ui/Spinner'
import { agoOrEmpty } from './format'

/**
 * Sertarul rapid din dreapta - `QuickActionsSidebar` din `public/js/general.js`
 * si machetele `views/version-3/quick_sidebar/{device,user,company,office,
 * owner,affected_devices}/index.php`, cu acelasi markup (`page-quick-sidebar-
 * wrapper`, `widget-collapse dark`, `quick-sidebar-device-avatar`), pe foaia
 * portalului.
 *
 * Se deschide de ORIUNDE, ca in portal: un clic pe `.quick-sidebar-device`,
 * `.quick-sidebar-user` / `.quick-sidebar-manager`, `.quick-sidebar-owner`,
 * `.quick-sidebar-company`, `.quick-sidebar-office` sau `.show-affected-devices`
 * (cu `data-id`) il umple; `a.close-sidebar` il inchide. Sta o singura data,
 * in Shell, langa `#app-footer`.
 *
 * Panoul de echipament are cele trei file ale portalului (Summary, Alarms,
 * Subscriptions) - aici duc la fisa masinii, fiindca panourile mici
 * (`quick/deviceSummary` s.a.) nu sunt desenate inca in v4 (PARTIAL in
 * inventar).
 */

export const OPEN_EVENT = 'od:quick-sidebar-open'

const TRIGGERS: Array<{ selector: string; kind: QuickKind; title: string }> = [
  { selector: '.quick-sidebar-device', kind: 'device', title: 'Device details' },
  { selector: '.quick-sidebar-user, .quick-sidebar-manager', kind: 'user', title: 'User details' },
  { selector: '.quick-sidebar-owner', kind: 'owner', title: 'Owner details' },
  { selector: '.quick-sidebar-company', kind: 'company', title: 'Company details' },
  { selector: '.quick-sidebar-office', kind: 'office', title: 'Office details' },
  { selector: '.show-affected-devices', kind: 'ticket-devices', title: 'Affected devices' },
  /* `#my_chat_btn` din antet: `MyChat.openChat`. */
  { selector: '#my_chat_btn', kind: 'chat', title: 'Conversation' },
  { selector: '.user-last-message', kind: 'chat-customer', title: 'Conversation' },
]

export function openQuickSidebar(kind: QuickKind, id: number) {
  document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { kind, id } }))
}

/** `get_status_dot`: bulina pe primele trei trepte, semnul de mirare pe ultimele doua. */
function CommDot({ comm, pulse }: { comm: Comm; pulse: string }) {
  if (comm.key === 'stale' || comm.key === 'lost') {
    return (
      <span className={`od-comm od-comm-${comm.key} ${pulse}`} title={comm.label}>
        <i className="fas fa-exclamation" aria-hidden="true"></i>
      </span>
    )
  }
  return <span className={`dots od-comm od-comm-${comm.key} ${pulse}`} title={comm.label}></span>
}

function or(value: string | null | undefined): string {
  return value ? value : 'n/a'
}

function dmy(value: string): string {
  const when = new Date(value.replace(' ', 'T'))
  if (isNaN(when.getTime())) return value
  return when.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ')
}

const LABEL = { color: 'rgba(255, 255, 255, 0.52)' } as const
const ROW = { padding: '10px 15px' } as const
const RIGHT = { maxWidth: 200, textAlign: 'right' } as const

function Row({ icon, label, value, wide = false }: { icon?: string; label: string; value: string; wide?: boolean }) {
  return (
    <li style={ROW}>
      <label style={LABEL}>
        {icon ? <i className={icon} aria-hidden="true"></i> : null} {label}
      </label>
      <span className="pull-right" style={wide ? RIGHT : undefined}>
        {value}
      </span>
    </li>
  )
}

function Box({ id, title, children }: { id: string; title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="widget-collapse dark">
      <header>
        <a href={`#${id}`} onClick={(e) => e.preventDefault()}>
          <i className="collapse-caret fa fa-angle-up"></i> {title}
        </a>
      </header>
      <section style={{ height: 'auto' }} className="collapse in" id={id}>
        <div className="collapse-boby" style={{ padding: 0 }}>
          {children}
        </div>
      </section>
    </div>
  )
}

function DeviceList({ devices, pulse }: { devices: QuickDeviceRow[]; pulse: string }) {
  return (
    <ul className="widget-slide-setting">
      {devices.map((d) => (
        <li key={d.id}>
          <label style={{ ...LABEL, width: '100%' }}>
            <CommDot comm={d.comm} pulse={pulse} /> <i className="fas fa-desktop" aria-hidden="true"></i> {d.name}
            <span style={{ float: 'right', color: 'rgba(255, 255, 255, 0.52)', fontSize: '70%' }}>{d.type}</span>
          </label>
          <small>{d.last_seen ? agoOrEmpty(d.last_seen) : ''}</small>
        </li>
      ))}
    </ul>
  )
}

function Avatar({ icon, title, sub }: { icon: string; title: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <>
      <div className="quick-sidebar-device-avatar">
        <a href="#" className="device-icon-rounded" onClick={(e) => e.preventDefault()}>
          <i className={icon} aria-hidden="true"></i>
        </a>
      </div>
      <div className="quick-sidebar-details">
        <p className="title">{title}</p>
        {sub}
      </div>
    </>
  )
}

function DevicePanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<QuickDevice>('device', id))
  if (!q.data) return <PanelState query={q} />
  const d = q.data
  const sheet = `/app/inventoree/device/${d.id}`
  return (
    <>
      <Avatar
        icon="fas fa-desktop"
        title={
          <>
            <CommDot comm={d.comm} pulse="pulsate-quick" /> {d.name}
          </>
        }
        sub={
          d.owner ? (
            <p className="owner">
              <i className="fa fa-user" aria-hidden="true"></i> {d.owner}
            </p>
          ) : (
            <p className="owner color-red">
              <i className="fa fa-user" aria-hidden="true"></i> Unassigned
            </p>
          )
        }
      />
      <div className="avatar-link btn-group btn-group-justified md-quick-tabs">
        <a className="btn btn-tour-summary" href={sheet} title="Summary">
          <i className="fa fa-briefcase"></i>
        </a>
        <a className="btn" href={`${sheet}#system-alarms`} title="Alarms">
          <i className="far fa-bell"></i>
          <em className="active"></em>
        </a>
        {d.subscriptions > 0 ? (
          <a className="btn" href={`${sheet}#subscriptions`} title="Subscriptions">
            <i className="fas fa-list"></i>
          </a>
        ) : null}
      </div>
      <div className="md-quick-content"></div>
    </>
  )
}

function UserPanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<QuickUser>('user', id))
  if (!q.data) return <PanelState query={q} />
  const u = q.data
  return (
    <>
      <Avatar icon="fa fa-user" title={`${u.first_name.toUpperCase()} ${u.last_name}`} sub={<span>(user)</span>} />
      <Box id="collapseSummary" title="User Info">
        <ul>
          <Row icon="fas fa-building" label="Company" value={or(u.company)} />
          <Row icon="far fa-envelope" label="Email" value={or(u.email)} />
          <Row icon="fa fa-phone" label="Phone" value={or(u.phone)} />
          <Row icon="fa fa-phone" label="Mobile Phone" value={or(u.mobile)} />
          <Row label="Position" value={or(u.position)} />
        </ul>
      </Box>
    </>
  )
}

function PersonBox({ id, title, person }: { id: string; title: string; person: QuickPerson }) {
  return (
    <Box id={id} title={title}>
      <ul>
        <Row icon="fa fa-user" label="Name" value={person.name} wide />
        <Row icon="far fa-envelope" label="Email" value={person.email} wide />
        <Row icon="fa fa-phone" label="Phone" value={person.phone} wide />
        <Row icon="fa fa-phone" label="Phone 2" value={person.phone2} wide />
      </ul>
    </Box>
  )
}

function CompanyPanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<QuickCompany>('company', id))
  if (!q.data) return <PanelState query={q} />
  const c = q.data
  return (
    <>
      <Avatar icon="fa fa-building" title={c.name.toUpperCase()} />
      <Box id="collapseSummary" title="Company Info">
        <ul>
          <Row icon="fa fa-map-pin" label="Adress" value={or(c.address)} wide />
          <Row icon="fa fa-phone" label="Phone" value={or(c.phone)} wide />
          <Row icon="far fa-envelope" label="Email" value={or(c.email)} />
          <Row icon="fa fa-child" label="Contact person" value={or(c.contact_person)} wide />
          <Row icon="far fa-calendar-check" label="Working Program" value={or(c.working_program)} wide />
          <Row icon="far fa-check-square" label="Created" value={c.created ? dmy(c.created) : 'n/a'} />
        </ul>
      </Box>
      {c.it_manager ? <PersonBox id="itManager" title="IT Manager" person={c.it_manager} /> : null}
      {c.it_responsible ? <PersonBox id="itResponsable" title="IT Responsable" person={c.it_responsible} /> : null}
    </>
  )
}

/** `quick_sidebar/office/index-1.js`: ceasul local si OPEN/CLOSED dupa program. */
function useOfficeClock(gmt: string, program: QuickOffice['program']) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(t)
  }, [])
  const offset = parseOffset(gmt)
  const local = new Date(now.getTime() + (now.getTimezoneOffset() + offset) * 60000)
  const hh = local.getHours()
  const time = `${String(hh % 12 === 0 ? 12 : hh % 12).padStart(2, '0')}:${String(local.getMinutes()).padStart(2, '0')}:${String(local.getSeconds()).padStart(2, '0')}`
  const day = local.toLocaleDateString('en-US', { weekday: 'long' })
  /* moment().format('E'): 1 = luni ... 7 = duminica. */
  const iso = String(local.getDay() === 0 ? 7 : local.getDay())
  const rule = program.find((p) => String(p.day) === iso)
  let open = false
  if (rule) {
    const minutes = local.getHours() * 60 + local.getMinutes()
    open = minutes >= toMinutes(rule.start_program) && minutes <= toMinutes(rule.end_program)
  }
  return { time, day, open }
}

/** `+02:00`, `2`, `-3.5` -> minute. */
function parseOffset(gmt: string): number {
  const m = /^([+-]?)(\d{1,2})(?::?(\d{2}))?$/.exec(gmt.trim())
  if (m) {
    const sign = m[1] === '-' ? -1 : 1
    return sign * (Number(m[2]) * 60 + Number(m[3] ?? 0))
  }
  const n = Number(gmt)
  return isNaN(n) ? 0 : Math.round(n * 60)
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':')
  return Number(h ?? 0) * 60 + Number(m ?? 0)
}

function OfficeClock({ office }: { office: QuickOffice }) {
  const clock = useOfficeClock(office.gmt, office.program)
  return (
    <>
      <span id="office-working-status" className={`label font-24 ${clock.open ? 'label-success' : 'label-danger'}`} style={{ marginTop: 6 }}>
        {clock.open ? 'OPEN' : 'CLOSED'}
      </span>
      <Box id="collapseClock" title="Local Office Time">
        <div className="align-center" id="clock" style={{ padding: 15 }}>
          <div className="label label-default font-12" id="office-week-day">
            {clock.day}
          </div>
          <div id="office-clock" style={{ textAlign: 'center', fontSize: 35 }}>
            <i className="far fa-clock" aria-hidden="true"></i> <span id="clock-display">{clock.time}</span>
            <p className="align-center font-12">{office.timezone}</p>
          </div>
        </div>
      </Box>
    </>
  )
}

function OfficePanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<QuickOffice>('office', id))
  if (!q.data) return <PanelState query={q} />
  const o = q.data
  return (
    <>
      <Avatar icon="fa fa-map-marker" title={o.name.toUpperCase()} />
      <OfficeClock office={o} />
      <Box id="collapseSummary" title="Office Info">
        <ul>
          <Row icon="fa fa-map-pin" label="Address" value={or(o.address)} wide />
          <Row icon="fa fa-map-pin" label="Address 2" value={or(o.address_2)} wide />
          <Row icon="fa fa-phone" label="Phone" value={or(o.phone)} wide />
          <Row icon="far fa-envelope" label="Email" value={or(o.email)} />
          <Row label="ZIP" value={or(o.zip)} />
          <Row label="Region" value={or(o.region)} />
          <Row label="District" value={or(o.district)} />
          <Row label="State" value={or(o.state)} />
          <Row label="City" value={or(o.city)} />
          <Row label="Manager" value={or(o.manager)} />
          <Row label="Latitude" value={or(o.latitude)} />
          <Row label="Longitude" value={or(o.longitude)} />
          <li style={ROW}>
            <label style={LABEL}>Work program</label>
            <span className="pull-right" style={{ whiteSpace: 'pre-line' }}>
              {or(o.work_program)}
            </span>
          </li>
        </ul>
      </Box>
    </>
  )
}

function OwnerPanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<QuickOwner>('owner', id))
  const [tab, setTab] = useState<'devices' | 'services' | 'subscriptions'>('devices')
  if (!q.data) return <PanelState query={q} />
  const o = q.data
  const cell = (label: string, value: string) => (
    <li style={ROW} className="col-md-6">
      <label style={LABEL}>{label}</label>
      <span className="pull-right">{value}</span>
    </li>
  )
  const tabLink = (key: typeof tab, label: string, count: number) => (
    <li className="nav-item">
      <a
        className={`nav-link header-link${tab === key ? ' active' : ''}`}
        href={`#${key}`}
        role="tab"
        style={{ borderBottom: 'solid 1px #DDDDDD', paddingTop: 0 }}
        onClick={(e) => {
          e.preventDefault()
          setTab(key)
        }}
      >
        {label} <span className="label label-success">{count}</span>
      </a>
    </li>
  )
  return (
    <>
      <div className="row">
        <div className="col-md-3" style={{ paddingLeft: 45 }}>
          <Avatar
            icon="fa fa-user"
            title={`${o.first_name.toUpperCase()} ${o.last_name}`}
            sub={
              <p className="owner">
                <i className="far fa-user" aria-hidden="true"></i> OWNER
              </p>
            }
          />
        </div>
        <div className="col-md-9">
          <ul>
            <li style={ROW} className="col-md-6">
              <label>
                <i className="fas fa-building" aria-hidden="true"></i> Company
              </label>
              <span className="pull-right text-white text">{or(o.company)}</span>
            </li>
            <li style={ROW} className="col-md-6">
              <label style={LABEL}>
                <i className="far fa-envelope" aria-hidden="true"></i> Email
              </label>
              <span className="text pull-right">{or(o.email)}</span>
            </li>
            <li style={ROW} className="col-md-6">
              <label style={LABEL}>
                <i className="fa fa-phone" aria-hidden="true"></i> Phone
              </label>
              <span className="pull-right">{or(o.phone)}</span>
            </li>
            <li style={ROW} className="col-md-6">
              <label style={LABEL}>
                <i className="fa fa-phone" aria-hidden="true"></i> Mobile Phone
              </label>
              <span className="pull-right">{or(o.mobile)}</span>
            </li>
            {cell('Position', or(o.position))}
            {cell('Address', or(o.address))}
            {cell('City', or(o.city))}
            {cell('State', or(o.state))}
            {cell('Zip', or(o.zip))}
            {cell('Created on', o.created ? dmy(o.created) : 'n/a')}
            {cell('Observations', or(o.description))}
          </ul>
        </div>
      </div>
      <ul className="nav nav-tabs md-quick-tabs" id="myTab" role="tablist">
        {tabLink('devices', 'Devices', o.devices.length)}
        {tabLink('services', 'Services', o.services.length)}
        {tabLink('subscriptions', 'Subscriptions', o.subscriptions.length)}
      </ul>
      <div className="tab-content" id="myTabContent" style={{ margin: 0, padding: 0 }}>
        <div className="tab-pane dark active" style={{ background: '#263242' }} role="tabpanel">
          <div className="collapse-boby" style={{ padding: 0 }}>
            {tab === 'devices' ? (
              <DeviceList devices={o.devices} pulse="pulsate-owner-device" />
            ) : tab === 'services' ? (
              <ul className="widget-slide-setting">
                {o.services.map((s, i) => (
                  <li className="col-md-6" key={i}>
                    <label>
                      {s.activated === 1 ? (
                        <span className="dots dots-danger faa-flash animated pulsate-owner-device" title="Activated"></span>
                      ) : (
                        <span className="dots dots-active faa-flash animated pulsate-owner-device"></span>
                      )}{' '}
                      Service: <span className="text">{s.service_name}</span>
                    </label>
                    <label className="pull-left">
                      Group: <span className="text">{s.group_name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="widget-slide-setting">
                {o.subscriptions.map((s, i) => (
                  <li className="col-md-6" key={i}>
                    <label>
                      {s.activated === 1 ? (
                        <span className="dots dots-danger faa-flash animated pulsate-owner-device" title="Activated"></span>
                      ) : (
                        <span className="dots dots-active faa-flash animated pulsate-owner-device"></span>
                      )}{' '}
                      Subscription: <span className="text">{s.workorder_id}</span>
                    </label>
                    <label className="pull-left">
                      SKU: <span className="text">{s.sku_name}</span>
                    </label>
                    <label>
                      Devices left: <span className="text">{s.devices_left}</span>
                    </label>
                    {s.devices.length > 0 ? (
                      <p>
                        <label>Devices</label>
                        {s.devices.map((d, k) => (
                          <span className="text-white" key={k}>
                            {k + 1}.{d.device_name} ({d.device_type_name})<br />
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function TicketDevicesPanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<{ devices: QuickDeviceRow[] }>('ticket-devices', id))
  if (!q.data) return <PanelState query={q} />
  return (
    <Box
      id="collapseDevices"
      title={
        <>
          Devices <span className="label label-success">{q.data.devices.length}</span>
        </>
      }
    >
      <DeviceList devices={q.data.devices} pulse="pulsate-owner-device" />
    </Box>
  )
}

/** `MyChat.buildList`: conversatiile cu mesaje necitite; un clic deschide clientul. */
function ChatPanel() {
  const q = useQuery({ ...quickPanelQuery<ChatConversation[]>('chat', 1), refetchInterval: 60 * 1000 })
  if (!q.data) return <PanelState query={q} />
  return (
    <ul className="widget-slide-setting" id="my_chat_list">
      {q.data.length === 0 ? (
        <li style={{ padding: 15 }} className="align-center">
          No conversation
        </li>
      ) : null}
      {q.data.map((c) => (
        <li className="user-last-message" data-customer={c.user_id} key={`${c.user_id}-${c.last_message_id}`} style={{ cursor: 'pointer' }}>
          <span style={{ float: 'right', color: 'rgba(255, 255, 255, 0.52)', fontSize: '70%' }}>
            {c.last_message ? agoOrEmpty(c.last_message.created) : ''}
          </span>
          <div style={{ position: 'relative' }}>
            <span className="badge bg-success" style={{ position: 'absolute', right: 0, top: 18, fontSize: 10, padding: '3px 5px' }}>
              {c.messages}
            </span>
            <div className="pull-left" style={{ marginRight: 10 }}>
              <i className="flaticon-user flaticon-20"></i>
            </div>
            <div className="pull-left" style={{ paddingTop: 6 }}>
              <span style={{ verticalAlign: 'super' }}>{c.user}</span>
            </div>
          </div>
          <div style={{ clear: 'both' }}>
            {c.last_message ? (
              <>
                <a target="_blank" className="open-ticket-window" href={`/app/easeedesk/ticket/${c.last_message.ticket_id}`} rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  <span style={{ fontSize: 10, color: '#d0b034' }}>#{c.last_message.ticket_id}</span>
                </a>{' '}
                <small>{c.last_message.message}</small>
              </>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}

/** `MyChat.buildConversation`: clientul si mesajele lui necitite, cu Back. */
function ChatCustomerPanel({ id }: { id: number }) {
  const q = useQuery(quickPanelQuery<ChatCustomer>('chat-customer', id))
  if (!q.data) return <PanelState query={q} />
  const c = q.data.customer
  return (
    <>
      <div style={{ padding: 15, color: '#fff', backgroundColor: '#32777e', fontSize: 16, fontWeight: 'bold' }}>
        <a
          id="back-to-chat-summary"
          className="color-white"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            openQuickSidebar('chat', 1)
          }}
        >
          <i className="fa fa-chevron-circle-left" aria-hidden="true"></i> Back
        </a>
      </div>
      <ul className="widget-slide-setting" id="my_chat_list">
        <li>
          <div style={{ position: 'relative' }}>
            <div className="pull-left" style={{ marginRight: 10 }}>
              <i className="flaticon-user flaticon-24"></i>
            </div>
            <div className="pull-left">
              <span style={{ verticalAlign: 'super' }}>
                {c.Prenume} {c.Nume}
              </span>
              <small>{c.Email}</small>
            </div>
            <div className="clear"></div>
          </div>
        </li>
        {q.data.messages.map((m) => (
          <li key={m.ID}>
            <div style={{ backgroundColor: '#ce8483', padding: 10, borderRadius: 10, color: '#fff', fontSize: 10, display: 'inline-block' }}>{m.message}</div>
            <div style={{ marginTop: 4, padding: '0 10px' }}>
              <a className="pull-left" target="_blank" href={`/app/easeedesk/ticket/${m.ticket_id}`} rel="noreferrer">
                <span style={{ color: '#d0b034', fontSize: 10 }}>#{m.ticket_id}</span>
              </a>
              <span className="pull-right" style={{ color: '#b1b1b1', fontSize: 10 }}>
                {m.created}
              </span>
              <div className="clear"></div>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}

function PanelState({ query }: { query: { isPending: boolean; error: unknown } }) {
  if (query.isPending) return <Spinner />
  const message = query.error instanceof ApiError && query.error.message ? query.error.message : 'Please try again later.'
  return (
    <div className="alert alert-danger" style={{ margin: 15 }}>
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> <strong>Error.</strong> {message}
    </div>
  )
}

const PANELS: Record<QuickKind, (props: { id: number }) => React.ReactElement> = {
  device: DevicePanel,
  user: UserPanel,
  company: CompanyPanel,
  office: OfficePanel,
  owner: OwnerPanel,
  'ticket-devices': TicketDevicesPanel,
  chat: ChatPanel,
  'chat-customer': ChatCustomerPanel,
}

export function QuickSidebar() {
  const [shown, setShown] = useState<{ kind: QuickKind; id: number } | null>(null)

  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null
      if (!target) return
      if (target.closest('a.close-sidebar')) {
        ev.preventDefault()
        setShown(null)
        return
      }
      for (const t of TRIGGERS) {
        const hit = target.closest<HTMLElement>(t.selector)
        if (!hit) continue
        const id = t.kind === 'chat' ? 1 : Number(hit.dataset.id ?? hit.dataset.customer)
        if (!(id > 0)) return
        ev.preventDefault()
        setShown({ kind: t.kind, id })
        return
      }
    }
    const onOpen = (ev: Event) => {
      const detail = (ev as CustomEvent<{ kind: QuickKind; id: number }>).detail
      if (detail && detail.id > 0) setShown({ kind: detail.kind, id: detail.id })
    }
    document.addEventListener('click', onClick)
    document.addEventListener(OPEN_EVENT, onOpen)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener(OPEN_EVENT, onOpen)
    }
  }, [])

  const Panel = shown ? PANELS[shown.kind] : null
  const title = shown ? TRIGGERS.find((t) => t.kind === shown.kind)?.title ?? '' : ''

  return (
    <div id="od-quick-sidebar" className={shown ? 'page-quick-sidebar-open' : ''}>
      <div className="page-quick-sidebar-wrapper quick-sidebar-navigation">
        <div className="page-quick-sidebar-actions">
          <span id="quick-sidebar-title" style={{ color: '#fff', padding: 13, fontSize: 16, fontWeight: 'bold', display: 'inline-block' }}>
            {title}
          </span>
          <a className="close-sidebar" href="#" onClick={(e) => e.preventDefault()}>
            <i className="fas fa-indent"></i>
          </a>
        </div>
        <div className="page-quick-sidebar avatar-slide" style={{ height: '100%', overflowY: 'auto' }}>
          {Panel && shown ? <Panel key={`${shown.kind}-${shown.id}`} id={shown.id} /> : null}
        </div>
      </div>
    </div>
  )
}
